/**
 * 004 — Payload canónico de la invitación (contrato del Design System).
 * Refleja specs/004-invitation-theming/diccionario-de-datos.md.
 * Contiene SOLO ✍️ (pareja) + 🎚 (interruptores) + 🔗 (enlace/invitado).
 * NO incluye ⚙️ (derivados → derived.ts) ni 🏛 (copy de sistema → system-catalog.ts).
 */

/** Los 7 estilos del DS (coinciden con las clases `theme-<id>` de los tokens). */
export type EstiloId =
  | 'costero'
  | 'elegante'
  | 'epoca'
  | 'moderno'
  | 'romantico'
  | 'rustico'
  | 'urbano';

export const ESTILOS: EstiloId[] = [
  'elegante',
  'rustico',
  'costero',
  'urbano',
  'romantico',
  'epoca',
  'moderno',
];

/** Modo del bloque de retratos (un solo interruptor de tres estados). */
export type RetratosModo = 'pareja' | 'individuales' | 'sin';

export interface Persona {
  nombreCorto: string;
  nombreCompleto?: string;
}

export interface EventoItem {
  tipo: 'religiosa' | 'civil' | 'recepcion';
  hora?: string;
  sede?: string;
  direccion?: string;
  /** ✍️ prellenado: si viene vacío, lo pone el sistema según el tipo. */
  nota?: string;
  mapaUrl?: string;
  /** Solo si el interruptor `fotosLugares` está encendido. */
  foto?: string;
}

export interface ItinerarioItem {
  /** 🏛 catálogo: id de momento (ceremonia, coctel, cena, fiesta…) o `'otro'` (libre). */
  momento: string;
  /** Texto libre del momento cuando `momento === 'otro'`. */
  momentoLibre?: string;
  hora?: string;
  detalle?: string;
}

export interface Vestimenta {
  /** 🏛 catálogo: código (formal, coctel, etiqueta-rigurosa…). */
  codigo: string;
  paleta?: string[];
  reservados?: string[];
}

export interface GiftSugerencia {
  nombre: string;
  precio?: number;
  tienda?: string;
  url?: string;
  descripcion?: string;
}

export interface Regalos {
  /** ✍️ prellenado. */
  mensaje?: string;
  tiendaUrl?: string;
  banco?: string;
  titular?: string;
  cuenta?: string;
  qrUrl?: string;
  moneda?: string;
  sugerencias?: GiftSugerencia[];
}

export interface FotosPayload {
  portada?: string;
  pareja?: string;
  novia?: string;
  novio?: string;
  vestimenta?: string;
  galeria?: string[];
  sedes?: Partial<Record<'religiosa' | 'civil' | 'recepcion', string>>;
}

/** Mapa de interruptores 🎚 (on/off por bloque). */
export interface Interruptores {
  retratos: RetratosModo;
  fotosLugares: boolean;
  fotoVestimenta: boolean;
  galeria: boolean;
  fotosInvitados: boolean;
  ceremoniaReligiosa: boolean;
  ceremoniaCivil: boolean;
  ninosPermitidos: boolean;
  coloresSugeridos: boolean;
  coloresReservados: boolean;
  regalos: boolean;
  mesaRegalos: boolean;
  transferencia: boolean;
  transferenciaQR: boolean;
  efectivoSobre: boolean;
  listaRegalos: boolean;
}

/** Invitado 🔗 — proviene de `access_code` (002), no de la URL. */
export interface InvitadoPayload {
  nombre: string;
  pases: number;
}

export interface InvitationPayload {
  estilo: EstiloId;
  boda: {
    fecha: string | null; // ISO; del Evento (006)
    ordenNombres?: 'bride_first' | 'groom_first'; // 008 · US10
  };
  novia: Persona;
  novio: Persona;
  historia?: { texto?: string; frase?: string };
  familia?: Array<{ rol: string; nombre: string }>;
  eventos?: EventoItem[];
  itinerario?: ItinerarioItem[];
  vestimenta?: Vestimenta;
  album?: { hashtag?: string }; // 009: el enlace externo se retira; el álbum es nativo.
  regalos?: Regalos;
  rsvp?: { fechaLimite?: string };
  musica?: { url?: string };
  fotos?: FotosPayload;
  interruptores: Interruptores;
  /** Presente solo cuando hay un `access_code` válido (render por invitado). */
  invitado?: InvitadoPayload;
}
